import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
} from "@mui/material";

import InfoIcon from "../../../../common/components/icons/InfoIcon";

export type SortableColumn =
  | "skuNumber"
  | "serialNumber"
  | "configurationName"
  | "isAllOnline";

export type SortDirection = "asc" | "desc";

interface IGroupedDevicesTableHeadRowProps {
  orderBy: SortableColumn;
  order: SortDirection;
  onSort: (column: SortableColumn) => void;
}

const GroupedDevicesTableHeadRow = ({
  orderBy,
  order,
  onSort,
}: IGroupedDevicesTableHeadRowProps): JSX.Element => {
  const createSortHandler = (column: SortableColumn) => () => {
    onSort(column);
  };

  return (
    <TableHead
      sx={{
        "& .MuiTableCell-head": {
          fontWeight: 600,
          fontSize: "12px",
          textTransform: "uppercase",
          backgroundColor: (theme: Theme) =>
            theme.palette.otherBackdropOverlay.main,
          color: (theme: Theme) => theme.palette.text.secondary,
          borderBottom: (theme: Theme) =>
            `1px solid ${theme.palette.otherOutlineBorder.main}`,
        },
        "& tr:first-of-type th:first-of-type": {
          borderTopLeftRadius: "12px",
        },
        "& tr:first-of-type th:last-of-type": {
          borderTopRightRadius: "12px",
        },
      }}
    >
      <TableRow>
        {/* Expand/Collapse icon column */}
        <TableCell
          sx={{
            width: "48px",
            minWidth: "48px",
          }}
        />
        {/* SKU column */}
        <TableCell
          sx={{ minWidth: 100 }}
          sortDirection={orderBy === "skuNumber" ? order : false}
        >
          <TableSortLabel
            active={orderBy === "skuNumber"}
            direction={orderBy === "skuNumber" ? order : "asc"}
            onClick={createSortHandler("skuNumber")}
          >
            SKU
          </TableSortLabel>
        </TableCell>
        {/* SKU Serial Number column */}
        <TableCell
          sx={{ minWidth: 140 }}
          sortDirection={orderBy === "serialNumber" ? order : false}
        >
          <TableSortLabel
            active={orderBy === "serialNumber"}
            direction={orderBy === "serialNumber" ? order : "asc"}
            onClick={createSortHandler("serialNumber")}
          >
            SKU SN
          </TableSortLabel>
        </TableCell>
        {/* Configuration column */}
        <TableCell
          sx={{ minWidth: 140 }}
          sortDirection={orderBy === "configurationName" ? order : false}
        >
          <TableSortLabel
            active={orderBy === "configurationName"}
            direction={orderBy === "configurationName" ? order : "asc"}
            onClick={createSortHandler("configurationName")}
          >
            CONFIGURATION
          </TableSortLabel>
        </TableCell>
        {/* Status column */}
        <TableCell
          sx={{ minWidth: 100 }}
          sortDirection={orderBy === "isAllOnline" ? order : false}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5em",
            }}
          >
            <TableSortLabel
              active={orderBy === "isAllOnline"}
              direction={orderBy === "isAllOnline" ? order : "asc"}
              onClick={createSortHandler("isAllOnline")}
            >
              STATUS
            </TableSortLabel>
            <Tooltip
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              title="Online only if all components in the group are online"
            >
              <Box>
                <InfoIcon />
              </Box>
            </Tooltip>
          </Box>
        </TableCell>
        {/* Actions column */}
        <TableCell sx={{ width: 60 }} />
      </TableRow>
    </TableHead>
  );
};

export default GroupedDevicesTableHeadRow;
